import { PageProps } from '@/types'
import { XMarkIcon } from '@heroicons/react/16/solid'
import { usePage } from '@inertiajs/react'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { useFlash } from '@/Components/Feedback/FlashProvider'

export default function FlashMessages() {
    const { current, clear } = useFlash()

    const { flash, errors } = usePage<PageProps>().props
    const [timeoutRemaining, setTimeoutRemaining] = useState(0)
    const [timer, setTimer] = useState<NodeJS.Timeout | null>(null)

    // Prefer Inertia flash when present; otherwise use provider message
    const hasInfo = Boolean(flash?.info)
    const hasSuccess = Boolean(flash?.success)
    const hasError = Boolean(flash?.error) || Object.keys(errors).length > 0

    const isError = hasError || (!hasError && current?.type === 'error')
    const isInfo = (!isError && hasInfo) || (!isError && !hasInfo && current?.type === 'info')
    const isSuccess = (!isError && !isInfo && hasSuccess) || (!isError && !hasInfo && !hasSuccess && current?.type === 'success')

    const timeoutTotalMs = 5000
    const isShowingMessage = isError || isInfo || isSuccess
    const lastStartedMessageIdentifier = useRef<string | null>(null)

    const errorHtml = (flash?.error as string) ?? (current?.type === 'error' ? current.message : '')
    const infoHtml = (flash?.info as string) ?? (current?.type === 'info' ? current.message : '')
    const successHtml = (flash?.success as string) ?? (current?.type === 'success' ? current.message : '')

    const activeMessageIdentifier = (() => {
        if (isError) {
            return `error:${errorHtml}:${JSON.stringify(errors)}`
        }

        if (isInfo) {
            return `info:${infoHtml}`
        }

        if (isSuccess) {
            return `success:${successHtml}`
        }

        return null
    })()

    useEffect(() => {
        if (!isShowingMessage || !activeMessageIdentifier) {
            lastStartedMessageIdentifier.current = null
            return
        }

        if (lastStartedMessageIdentifier.current === activeMessageIdentifier) {
            return
        }

        lastStartedMessageIdentifier.current = activeMessageIdentifier
        setTimeoutRemaining(timeoutTotalMs)
        startTimer()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowingMessage, activeMessageIdentifier])

    function startTimer() {
        if (timer) {
            clearInterval(timer)
        }

        const intervalId = setInterval(() => {
            setTimeoutRemaining((prev) => {
                if (prev <= 5) {
                    clearInterval(intervalId)
                    return 0
                }
                return prev - 5
            })
        }, 5)

        setTimer(intervalId)
    }

    const reset = () => {
        setTimeoutRemaining(0)

        if (timer) {
            clearInterval(timer)
        }
        // Clear provider message if that's what is being shown
        clear()
    }

    function onMouseEnter() {
        if (timer) {
            clearInterval(timer)
        }
    }

    function onMouseLeave() {
        startTimer()
    }

    const barClass = () => {
        if (isError) return 'bg-red-400'
        if (isInfo) return 'bg-blue-400'
        if (isSuccess) return 'bg-green-400'
        return ''
    }

    const isVisible = isShowingMessage && timeoutRemaining > 0

    return (
        <div aria-live="assertive" className="pointer-events-none fixed inset-0 z-[100] flex items-end px-4 py-6 sm:items-start sm:p-6">
            <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
                {isVisible && (
                    <div
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        className={clsx(
                            'pointer-events-auto w-full max-w-sm overflow-hidden rounded-md shadow-lg ring-1 ring-stone-800/10',
                            isError ? 'bg-red-50' : '',
                            isInfo ? 'bg-blue-50' : '',
                            isSuccess ? 'bg-green-50' : ''
                        )}
                    >
                        <div className="relative p-4">
                            <div className="flex items-start">
                                <div className="ml-3 w-0 flex-1">
                                    {isError && (
                                        <>
                                            <p className="text-base font-medium text-black">Ein Fehler ist aufgetreten...</p>
                                            <p className="mt-1 text-sm leading-tight text-gray-700" dangerouslySetInnerHTML={{ __html: errorHtml }} />

                                            {Object.values(errors).length > 0 && (
                                                <p className="mt-1 text-sm leading-tight text-gray-700">Bitte überprüfe deine Eingaben.</p>
                                            )}
                                        </>
                                    )}
                                    {isSuccess && (
                                        <>
                                            <p className="text-base font-medium text-black">Erfolg!</p>
                                            <p
                                                className="mt-1 text-sm leading-tight text-gray-700"
                                                dangerouslySetInnerHTML={{ __html: successHtml }}
                                            />
                                        </>
                                    )}
                                    {isInfo && (
                                        <>
                                            <p className="text-base font-medium text-black">Info</p>
                                            <p className="mt-1 text-sm leading-tight text-gray-700" dangerouslySetInnerHTML={{ __html: infoHtml }} />
                                        </>
                                    )}
                                </div>
                                <div className="ml-4 flex shrink-0">
                                    <button onClick={reset} className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-none">
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="h-0.5 bg-gray-200">
                            <div className={`h-0.5 ${barClass()}`} style={{ width: `${(timeoutRemaining / timeoutTotalMs) * 100}%` }} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
